import { useT, type MessageKey } from '../lib/i18n';
import { CheckIcon, PlayIcon, TrophyIcon } from '../components/Icons';
import { FullscreenToggle } from '../components/FullscreenToggle';
import { LanguageSelect } from '../components/LanguageSelect';
interface Props {
  error?: string | null;
  onMatch: () => void;
  onPractice: () => void;
  onLeaderboards: () => void;
  onCameras: () => void;
  onSpectate: () => void;
  onImpressum: () => void;
  onPrivacy: () => void;
}

/** A line inside a mode card. `soon` marks something not built yet. */
interface Point {
  key: MessageKey;
  soon?: boolean;
}

const MATCH_POINTS: Point[] = [
  { key: 'home.matchPlayers' },
  { key: 'home.matchFrames' },
  { key: 'home.matchRated' },
  { key: 'home.matchBreak' },
  { key: 'home.matchTimed' },
];

const PRACTICE_POINTS: Point[] = [
  { key: 'home.practicePlayers' },
  { key: 'home.practiceUnrated' },
  { key: 'home.practiceNoBreak' },
  { key: 'home.practiceCoaching', soon: true },
  { key: 'home.practiceChallengeRank', soon: true },
];

export function HomeScreen({ error, onMatch, onPractice, onLeaderboards, onCameras, onSpectate, onImpressum, onPrivacy }: Props) {
  const t = useT();

  return (
    // A column, not a canvas with things floated onto it.
    //
    // The rankings button used to be `absolute bottom-6 right-6` inside this
    // scroller, which meant its position was decided by the *content* box, not
    // the screen: on anything short enough to scroll it drifted up over the
    // mode cards, and it landed somewhere different on every device. It is a
    // row in the flow now —it cannot overlap what it is laid out after.
    <main className="home-screen flex h-full w-full flex-col overflow-y-auto">
      <header className="flex shrink-0 items-center justify-end p-4">
        <FullscreenToggle
          className="btn-ghost mr-auto px-3 py-2 text-[0.72rem] font-bold uppercase tracking-[0.12em] text-white/85"
        />
        <LanguageSelect />
      </header>
      {error && <p role="alert" className="px-5 text-center text-sm text-red-300">{error}</p>}

      {/* The hero and the mode cards take the middle and are centred in
          whatever is left, full stop —they are what somebody came here to
          press. */}
      <div className="home-container home-content flex flex-1 flex-col items-center justify-center gap-6 pb-4">
        <div className="home-hero w-full text-center">
          <h1 className="home-title font-semibold leading-none tracking-[-0.045em] text-white">
            Auto-ScoreBoard
          </h1>
          <p
            className="home-tagline mx-auto mt-4 max-w-lg text-white/50"
          >
            {t('app.tagline')}
          </p>
        </div>

        <div className="home-modes grid w-full gap-5 sm:grid-cols-2">
          <ModeCard
            title={t('home.match')}
            points={MATCH_POINTS}
            onClick={onMatch}
            accent="#40cbe0"
          />
          <ModeCard
            title={t('home.practice')}
            points={PRACTICE_POINTS}
            onClick={onPractice}
            accent="#ff9f0a"
          />
        </div>
      </div>

      {/* Rankings are worth a look but not worth competing with the only two
          buttons that lead anywhere, so they are a single quiet row under the
          cards rather than a third card beside them. */}
      <footer className="home-container flex shrink-0 flex-col items-center gap-3 pb-6 pt-2">
        <div className="home-links grid w-full gap-5">
          <button
            type="button"
            onClick={onLeaderboards}
            className="panel group flex flex-1 items-center gap-3 px-6 py-4 text-left transition
                       hover:brightness-125"
          >
            <TrophyIcon className="h-8 w-8 shrink-0 text-ball-yellow" />
            <span className="min-w-0 flex-1">
              <span className="home-link-title block text-base font-bold text-white">{t('ranking.button')}</span>
            </span>
            <span className="text-lg text-white/40 transition group-hover:translate-x-0.5 group-hover:text-white/70">
              →
            </span>
          </button>
          <button type="button" onClick={onSpectate}
            className="panel group flex min-w-0 items-center gap-3 px-6 py-4 text-left transition hover:brightness-125">
            <PlayIcon className="h-8 w-8 shrink-0 text-green-400" />
            <span className="min-w-0 flex-1">
              <span className="home-link-title block text-base font-bold text-white">{t('spectator.title')}</span>
            </span>
          </button>
          <button
            type="button"
            onClick={onCameras}
            className="panel group flex flex-1 items-center gap-3 px-6 py-4 text-left transition
                       hover:brightness-125"
          >
            <span className="shrink-0 text-2xl">📷</span>
            <span className="min-w-0 flex-1">
              <span className="home-link-title block text-base font-bold text-white">{t('camera.title')}</span>
            </span>
            <span className="text-lg text-white/40 transition group-hover:translate-x-0.5 group-hover:text-white/70">
              →
            </span>
          </button>
        </div>
        <div className="text-sm text-white/45">
          <button type="button" onClick={onImpressum} className="hover:text-white/80">
            Impressum
          </button>
          <span className="px-2">·</span>
          <button type="button" onClick={onPrivacy} className="hover:text-white/80">
            Datenschutz
          </button>
        </div>
      </footer>
    </main>
  );
}

function ModeCard({
  title,
  points,
  onClick,
  accent,
}: {
  title: string;
  points: Point[];
  onClick: () => void;
  accent: string;
}) {
  const t = useT();

  return (
    <button
      type="button"
      onClick={onClick}
      className="home-mode panel group flex min-h-[22rem] flex-col items-stretch gap-0 p-8 text-left
                 transition hover:-translate-y-1"
      style={{
        boxShadow: [
          `inset 0 0 0 1px ${accent}38`,
          'inset 0 1px 0 0 rgba(255,255,255,0.06)',
          '0 18px 44px -24px rgba(0,0,0,0.95)',
        ].join(', '),
        background: `linear-gradient(180deg, ${accent}16, rgba(20,22,28,0.86) 55%)`,
      }}
    >
      {/* The title alone. An icon and a one-line description said nothing the
          word "Match" does not, and the points below are the part that
          actually helps somebody choose. */}
      <span
        className="text-5xl font-semibold uppercase leading-none tracking-[-0.03em]"
        style={{ color: accent }}
      >
        {title}
      </span>

      <ul className="mt-6 flex w-full flex-col gap-2.5 border-t border-white/10 pt-6 text-sm font-normal text-white/80">
        {points.map(({ key, soon }) => (
          <li key={key} className={`flex items-start gap-2.5 ${soon ? 'opacity-50' : 'opacity-95'}`}>
            {soon ? (
              <span aria-hidden className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-current" />
            ) : (
              <span style={{ color: accent }} className="mt-0.5 shrink-0">
                <CheckIcon className="h-4 w-4" />
              </span>
            )}
            <span className="flex-1">
              {t(key)}
              {soon && (
                <span className="ml-2 rounded-full bg-black/40 px-2 py-0.5 text-[0.65rem] uppercase tracking-wider">
                  {t('home.soon')}
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>

      {/* Pushed to the bottom, so the card is a card rather than a list with
          space left over, and so both cards end on the same line. */}
      <span
        className="mt-auto flex items-center gap-2 pt-6 text-base font-bold uppercase tracking-wider"
        style={{ color: accent }}
      >
        {t('home.start')}
        <span aria-hidden className="transition group-hover:translate-x-1">
          →
        </span>
      </span>
    </button>
  );
}
